/**
 * Rules for the Template Library that do not need React: the channel a row
 * is filed under, the rows Amazon Pinpoint holds, the WhatsApp (Meta) status
 * tag, and the sort orders.
 */
import { metaStatus } from './pipelines'

/**
 * A row's `comm_type` in the one form the library compares: trimmed and
 * lowercase. GET /comms/templates returns the raw rows (the model's own
 * lower-casing does not run on that path), and the filter, the tags, the
 * status menu and the download all compare it with lowercase names.
 */
export const normaliseCommType = (row) =>
    row && typeof row.comm_type === 'string'
        ? { ...row, comm_type: row.comm_type.trim().toLowerCase() }
        : row

// Marks a row that lives in Amazon Pinpoint, not in CommTemplates.
export const PINPOINT = 'pinpoint'

/** True for a row listed from Amazon Pinpoint (read-only here). */
export const isPinpoint = (row) => row?.source === PINPOINT

/**
 * A library row for one Amazon Pinpoint SMS template (an item of
 * GET /comms/templates/pinpoint/sms, `response.Item`).
 *
 * The one-time password and demo flows send these; they have no
 * CommTemplates row, so they are listed read-only, as the old app did: an
 * id of their own, the SMS channel, and `created_on` in epoch seconds like
 * every other row (Pinpoint answers an ISO date).
 *
 * @param {{TemplateName: string, CreationDate?: string, LastModifiedDate?: string}} item
 * @returns {object}
 */
export const pinpointRow = (item) => {
    const created = Date.parse(item?.CreationDate ?? '')
    return {
        id: `${PINPOINT}-${item?.TemplateName}`,
        template_name: item?.TemplateName ?? '',
        comm_type: 'sms',
        source: PINPOINT,
        created_on: Number.isNaN(created)
            ? undefined
            : Math.floor(created / 1000),
    }
}

// WhatsApp's template states, by what they mean for a send. A template that
// is not approved at Meta does not send, whatever its comms status says.
// The library's tag tones for helpers/pipelines metaStatus's tones.
const TAG_TONE = {
    ok: 'success',
    waiting: 'warning',
    blocked: 'error',
    none: 'neutral',
}

/**
 * The WhatsApp (Meta) status tag of a template, or null when it has none.
 *
 * The status is read by helpers/pipelines `metaStatus`, the one reading the
 * pipeline wizard uses too, so both say the same words: approved reads as
 * success; pending (and in appeal) as a warning; rejected, disabled, paused,
 * archived and deleted as an error, because the template cannot send.
 * Nothing when the field is empty: the old app printed PENDING then, which
 * was not true. A value WhatsApp adds later shows as is, in a neutral tone.
 *
 * @param {{comm_type: string, external_template_status?: string}} template
 * @returns {{tone: string, label: string}|null}
 */
export const metaStatusTag = (template) => {
    const meta = metaStatus(template)
    if (!meta || meta.key === 'none') return null
    return { tone: TAG_TONE[meta.tone], label: meta.label }
}

export const SORT_OPTIONS = [
    { id: 1, label: 'Newest', value: 'newest' },
    { id: 2, label: 'Oldest', value: 'oldest' },
    { id: 3, label: 'Name A to Z', value: 'name' },
    { id: 4, label: 'Status', value: 'status' },
]

// The Status sort: what sends first, then what waits, then what does not.
const STATUS_ORDER = [
    'approved',
    'active',
    'approval_awaited',
    'disapproved',
    'inactive',
]
const statusRank = (row) => {
    const rank = STATUS_ORDER.indexOf(row?.status)
    return rank === -1 ? STATUS_ORDER.length : rank
}
const createdOn = (row) =>
    typeof row?.created_on === 'number' ? row.created_on : 0
const byNewest = (a, b) => createdOn(b) - createdOn(a)
const byName = (a, b) =>
    String(a?.template_name ?? '').localeCompare(
        String(b?.template_name ?? ''),
        undefined,
        { sensitivity: 'base', numeric: true }
    )

/**
 * A new list of `rows` in the chosen order.
 *
 * Dates compare as the numeric `created_on`, never a formatted date (the old
 * library sorted "1/10/2026" before "1/2/2026"). A row with no date sorts as
 * the oldest. Name ignores case and reads digits as numbers; Status puts
 * what sends first, then pending, rejected and inactive, newest first within
 * each. An unknown order keeps newest first.
 *
 * @param {object[]} rows
 * @param {'newest'|'oldest'|'name'|'status'} order
 * @returns {object[]}
 */
export const sortTemplates = (rows, order) => {
    const list = [...(rows ?? [])]
    if (order === 'oldest') return list.sort((a, b) => byNewest(b, a))
    if (order === 'name')
        return list.sort((a, b) => byName(a, b) || byNewest(a, b))
    if (order === 'status')
        return list.sort(
            (a, b) => statusRank(a) - statusRank(b) || byNewest(a, b)
        )
    return list.sort(byNewest)
}
